import { useState } from 'react';
import { Box, Card, LinearProgress, Stack, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { ReportAggregateBucket, ReportAggregatesFilters } from '@/api/models';
import { ApiRequestError } from '@/api/errors';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn, type DataGridSummaryRow } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { FiltersPopover } from '@/components/shell/FiltersPopover';
import { LoadingState } from '@/components/shell/LoadingState';
import { PageTitle } from '@/components/shell/PageTitle';
import { PillTabs } from '@/components/shell/PillTabs';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { payApiClient } from '@/api/client';
import { DateRangeFields } from '@/features/operations/filters/DateRangeFields';
import { downloadBlob } from '@/utils/download';
import { formatMinorUnits } from '@/utils/amount';
import { useApiResource } from '@/hooks/useApiResource';
import {
  buildBreakdownCsv,
  buildBreakdownRows,
  buildBreakdownTotals,
  currentYearStartIso,
  sortBreakdownRows,
  statisticsCsvFilename,
  summarizeAnnualUsage,
  type BreakdownRow,
  type BreakdownSortKey,
  type BreakdownTotalsLine,
  type BreakdownViewMode,
  type SortDirection,
  type StatsGrouping,
} from './domain';

const numberFormat = new Intl.NumberFormat('es-ES');
const percentFormat = new Intl.NumberFormat('es-ES', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * The aggregates request behind one statistics view. Every view uses a
 * single grouping dimension so each operation lands in exactly one bucket
 * (plus the per-currency split); `gateway` is the cheapest such dimension
 * for the views that only need totals.
 */
function aggregatesFilters(grouping: StatsGrouping, dateFrom?: string, dateTo?: string): ReportAggregatesFilters {
  return {
    dateFrom,
    dateTo,
    groupBy: [grouping === 'all' ? 'gateway' : grouping],
  };
}

/**
 * The reason this page exists: effective card charges since 1 January
 * against the contractual 300,000/year ceiling — only charged intents count,
 * never abandoned or declined attempts. One slim strip — count, share used
 * and remaining on a single row, a thin progress bar beneath — so the
 * breakdown below keeps the screen. Warning tone from 80% of the limit,
 * error tone once it is exceeded; the window is fixed to the current year
 * regardless of the breakdown filters.
 */
function AnnualCounterCard() {
  const translate = useTranslate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.getReportAggregates(aggregatesFilters('all', currentYearStartIso())),
    'statistics-annual',
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="card" />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied' || error.errorCode === 'tenant_not_authorized') {
      return (
        <Card>
          <AccessDenied body={error.errorCode === 'tenant_not_authorized' ? error.message : undefined} />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.statistics.annual.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  const usage = summarizeAnnualUsage(data?.buckets ?? []);
  const overLimit = usage.ratio >= 1;
  const barColor = overLimit ? 'error' : usage.warning ? 'warning' : 'primary';

  return (
    <Card sx={{ px: 2.5, py: 1.5 }}>
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="baseline" columnGap={1.5} rowGap={0.25} flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {translate('pay.statistics.annual.title', { year: new Date().getFullYear() })}
          </Typography>
          <Typography variant="h6" component="p" sx={{ fontWeight: 700, fontFamily: (theme) => theme.custom.fontFamilyMono, lineHeight: 1.2 }}>
            {numberFormat.format(usage.count)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('pay.statistics.annual.ofLimit', { limit: numberFormat.format(usage.limit) })}
          </Typography>
          <Box sx={{ flex: '1 1 auto' }} />
          <Typography variant="body2" color="text.secondary">
            {translate('pay.statistics.annual.percentUsed', { percent: percentFormat.format(usage.ratio) })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('pay.statistics.annual.remaining', { remaining: numberFormat.format(usage.remaining) })}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, usage.ratio * 100)}
          color={barColor}
          sx={{ height: 4, borderRadius: 999 }}
        />
        {usage.warning && (
          <Typography variant="body2" sx={{ color: (theme) => (overLimit ? theme.palette.error.main : theme.palette.warning.main), fontWeight: 600 }}>
            {translate(overLimit ? 'pay.statistics.annual.overLimit' : 'pay.statistics.annual.warning')}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

function bucketTitle(grouping: StatsGrouping, bucket: ReportAggregateBucket, translate: (key: string) => string): string {
  if (grouping === 'all') return translate('pay.statistics.breakdown.totalsTitle');
  if (grouping === 'tenant') {
    if (!bucket.tenantCode) return translate('pay.operations.aggregates.bucketUnlabelled');
    return bucket.tenantName ? `${bucket.tenantName} (${bucket.tenantCode})` : bucket.tenantCode;
  }
  // The null-tag bucket collects the operations of tenants carrying no tags.
  return bucket.tag ?? translate('pay.statistics.breakdown.untagged');
}

/**
 * Renders one totals line into the summary-row cell map the grid pins under
 * the data rows. The map covers every column of the extended view; the grid
 * renders only the cells whose column the active view actually shows, so the
 * overall percentage appears in the extended view alone.
 */
function totalsSummaryRow(line: BreakdownTotalsLine, translate: (key: string) => string): DataGridSummaryRow {
  return {
    key: line.currency ?? 'overall',
    cells: {
      bucket: translate(
        line.currency === null ? 'pay.statistics.table.totalsOverallLabel' : 'pay.statistics.table.totalsLabel',
      ),
      currency: line.currency ?? '',
      totalCount: numberFormat.format(line.totalCount),
      successCount: numberFormat.format(line.successCount),
      successRate: percentFormat.format(line.successRatio),
      // The overall line carries no amounts — they are never summed across currencies.
      totalAmount:
        line.currency === null || line.totalAmount === null ? '—' : formatMinorUnits(line.totalAmount, line.currency),
    },
  };
}

/**
 * The breakdown values table — one row per bucket and currency, same rows
 * the CSV export writes. The simple view shows bucket, currency, charges and
 * amount; the extended view adds the started-operations count and the
 * success rate. Every visible column header sorts the rows client-side (the
 * full result set is already in hand), defaulting to charges descending in
 * both views; the totals rows stay pinned at the bottom whatever the sort —
 * one per currency, plus an overall line when several currencies are
 * displayed.
 */
function BreakdownTable({ rows, view }: { rows: BreakdownRow[]; view: BreakdownViewMode }) {
  const translate = useTranslate();
  const [sortKey, setSortKey] = useState<BreakdownSortKey>('successCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Same toggle convention as the operations list: a new column starts
  // ascending, a second click on the active column flips the direction.
  const sortable = (key: BreakdownSortKey) => ({
    active: sortKey === key,
    direction: sortKey === key ? sortDirection : ('asc' as const),
    onToggle: () => {
      if (sortKey === key) {
        setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDirection('asc');
      }
    },
  });

  const extended = view === 'extended';

  const columns: Array<DataGridColumn<BreakdownRow>> = [
    {
      key: 'bucket',
      label: translate('pay.statistics.table.columnBucket'),
      sortable: sortable('label'),
      render: (row) => row.label,
    },
    {
      key: 'currency',
      label: translate('pay.statistics.table.columnCurrency'),
      mono: 'code',
      sortable: sortable('currency'),
      render: (row) => row.currency,
    },
    ...(extended
      ? [
          {
            key: 'totalCount',
            label: translate('pay.statistics.table.columnStarted'),
            align: 'right',
            mono: 'value',
            sortable: sortable('totalCount'),
            render: (row) => numberFormat.format(row.totalCount),
          } satisfies DataGridColumn<BreakdownRow>,
        ]
      : []),
    {
      key: 'successCount',
      label: translate('pay.statistics.table.columnCharges'),
      align: 'right',
      mono: 'value',
      mobileVisible: true,
      sortable: sortable('successCount'),
      render: (row) => numberFormat.format(row.successCount),
    },
    ...(extended
      ? [
          {
            key: 'successRate',
            label: translate('pay.operations.aggregates.fieldSuccessRate'),
            align: 'right',
            mono: 'value',
            sortable: sortable('successRatio'),
            render: (row) => percentFormat.format(row.successRatio),
          } satisfies DataGridColumn<BreakdownRow>,
        ]
      : []),
    {
      key: 'totalAmount',
      label: translate('pay.operations.aggregates.fieldTotalAmount'),
      align: 'right',
      mono: 'value',
      mobileVisible: true,
      sortable: sortable('totalAmount'),
      render: (row) => formatMinorUnits(row.totalAmount, row.currency),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={sortBreakdownRows(rows, sortKey, sortDirection)}
      getRowKey={(row) => `${row.label} ${row.currency}`}
      summaryRows={buildBreakdownTotals(rows).map((line) => totalsSummaryRow(line, translate))}
    />
  );
}

function BreakdownView({
  grouping,
  filters,
  view,
}: {
  grouping: StatsGrouping;
  filters: ReportAggregatesFilters;
  view: BreakdownViewMode;
}) {
  const translate = useTranslate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.getReportAggregates(filters),
    JSON.stringify(filters),
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="card" />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied' || error.errorCode === 'tenant_not_authorized') {
      return (
        <Card>
          <AccessDenied body={error.errorCode === 'tenant_not_authorized' ? error.message : undefined} />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.statistics.breakdown.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  const buckets = data?.buckets ?? [];
  if (buckets.length === 0) {
    return (
      <Card>
        <EmptyState
          title={translate('pay.statistics.breakdown.emptyTitle')}
          body={translate('pay.statistics.breakdown.emptyBody')}
        />
      </Card>
    );
  }

  const rows = buildBreakdownRows(buckets, (bucket) => bucketTitle(grouping, bucket, translate));

  // Keyed by view so switching resets the sort to the view's default.
  return <BreakdownTable key={view} rows={rows} view={view} />;
}

/**
 * Statistics page ("Estadísticas"): the slim annual card-charges counter
 * against the 300,000/year limit, and a breakdown of the same aggregates
 * surface — a sortable values table with pinned totals, in a simple
 * (charges + amount) or extended (adds started operations and success rate)
 * view — grouped by establishment, by tenant tag (contract `groupBy=tag`,
 * with the untagged bucket named), or as overall totals. "Exportar" writes
 * the current breakdown (grouping + date range + view) as a client-built
 * CSV for es-ES Excel, totals lines included. Read-only over
 * `GET /v1/admin/reports/aggregates` — same access model as the operations
 * aggregates view.
 */
export function StatisticsPage() {
  const translate = useTranslate();
  const notify = useNotify();
  const defaultDateFrom = currentYearStartIso();
  const [grouping, setGrouping] = useState<StatsGrouping>('tenant');
  const [view, setView] = useState<BreakdownViewMode>('simple');
  const [dateFrom, setDateFrom] = useState<string | undefined>(defaultDateFrom);
  const [dateTo, setDateTo] = useState<string | undefined>(undefined);
  const [refreshToken, setRefreshToken] = useState(0);
  const [exporting, setExporting] = useState(false);

  const activeCount = (dateFrom && dateFrom !== defaultDateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  async function handleExport() {
    setExporting(true);
    try {
      const response = await payApiClient.getReportAggregates(aggregatesFilters(grouping, dateFrom, dateTo));
      const rows = buildBreakdownRows(response.buckets ?? [], (bucket) => bucketTitle(grouping, bucket, translate));
      const csv = buildBreakdownCsv(rows, {
        bucket: translate('pay.statistics.table.columnBucket'),
        currency: translate('pay.statistics.table.columnCurrency'),
        totalCount: translate('pay.statistics.table.columnStarted'),
        successCount: translate('pay.statistics.table.columnCharges'),
        successRate: translate('pay.operations.aggregates.fieldSuccessRate'),
        totalAmount: translate('pay.operations.aggregates.fieldTotalAmount'),
      }, buildBreakdownTotals(rows), {
        totals: translate('pay.statistics.table.totalsLabel'),
        overall: translate('pay.statistics.table.totalsOverallLabel'),
      }, view);
      downloadBlob(
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        statisticsCsvFilename(grouping, dateFrom, dateTo),
      );
      notify(translate('pay.statistics.exportStarted'), { type: 'info' });
    } catch (caught) {
      const message = caught instanceof ApiRequestError ? caught.message : translate('pay.statistics.exportError');
      notify(message, { type: 'error' });
    } finally {
      setExporting(false);
    }
  }

  return (
    <Stack spacing={1.5}>
      <PageTitle title={translate('pay.statistics.pageTitle')} subtitle={translate('pay.statistics.pageSubtitle')} />
      <AnnualCounterCard key={`annual-${refreshToken}`} />
      <SectionHeader
        left={
          <Stack direction="row" alignItems="center" columnGap={2} rowGap={1} flexWrap="wrap">
            <PillTabs
              value={grouping}
              onChange={(next) => setGrouping(next as StatsGrouping)}
              options={[
                { value: 'tenant', label: translate('pay.statistics.tabs.tenant') },
                { value: 'tag', label: translate('pay.statistics.tabs.tag') },
                { value: 'all', label: translate('pay.statistics.tabs.all') },
              ]}
            />
            <PillTabs
              value={view}
              onChange={(next) => setView(next as BreakdownViewMode)}
              options={[
                { value: 'simple', label: translate('pay.statistics.views.simple') },
                { value: 'extended', label: translate('pay.statistics.views.extended') },
              ]}
            />
          </Stack>
        }
        filters={
          <FiltersPopover
            activeCount={activeCount}
            onClear={() => {
              setDateFrom(defaultDateFrom);
              setDateTo(undefined);
            }}
          >
            <DateRangeFields
              fromLabel={translate('pay.operations.filters.dateFrom')}
              toLabel={translate('pay.operations.filters.dateTo')}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChangeFrom={setDateFrom}
              onChangeTo={setDateTo}
            />
          </FiltersPopover>
        }
        onRefresh={() => setRefreshToken((token) => token + 1)}
        onExport={exporting ? undefined : handleExport}
      />
      <BreakdownView
        key={`breakdown-${refreshToken}`}
        grouping={grouping}
        filters={aggregatesFilters(grouping, dateFrom, dateTo)}
        view={view}
      />
    </Stack>
  );
}
